import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaSave } from 'react-icons/fa';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import '../Patients/CreatePatient.css'; // Assuming you're using the same CSS
import AccessControl from '../AccessControl';
import LinkMap from '../LinkMap/LinkMap';

const InventoryDetails = () => {
    const { id } = useParams(); // Get the inventory item ID from the URL
    const [inventoryItem, setInventoryItem] = useState(null);
    const [stores, setStores] = useState([]); // State to store the list of stores
    const [categories, setCategories] = useState([]); // State to store the list of categories
    const [loading, setLoading] = useState(true);
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [isEditable, setIsEditable] = useState(false);
    const [successModalVisible, setSuccessModalVisible] = useState(false);
    const linkData = [
        { label: 'Inventory', path: '/inventory' },
        { label: 'Inventory Details', path: '/inventory-details' },
    ];

    // Fetch inventory item data based on ID
    useEffect(() => {
        const fetchInventoryItem = async () => {
            try {
                const response = await fetch(`https://api.hgcdental.clinic/api/inventory/${id}`);
                if (!response.ok) throw new Error('Failed to fetch inventory item');
                const data = await response.json();
                // Ensure Category field is initialized
                setInventoryItem({
                    ...data,
                    Category: data.Category || "", // Set to empty string if null/undefined
                });
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchInventoryItem();
    }, [id]);

    // Fetch stores from the backend
    useEffect(() => {
        const fetchStores = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/stores');
                if (!response.ok) throw new Error('Failed to fetch stores');
                const data = await response.json();
                setStores(data);
            } catch (error) {
                console.error(error);
            }
        };
        fetchStores();
    }, []);

    // Fetch categories from the backend
    // Fetch categories from the backend
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/category');
                if (!response.ok) throw new Error('Failed to fetch categories');
                const data = await response.json();

                // Filter out archived categories (isArchived === true)
                const activeCategories = data.filter(category => !category.isArchived);

                setCategories(activeCategories);
            } catch (error) {
                console.error(error);
            }
        };
        fetchCategories();
    }, []);

    const handleSaveClick = async () => {
        try {
            // Create a clean payload with only the fields the server expects
            if (!inventoryItem.Name || !inventoryItem.Quantity || !inventoryItem.Store || !inventoryItem.Category) {
                alert("Please fill all required fields");
                return;
            }

            const payload = {
                Name: inventoryItem.Name,
                Quantity: inventoryItem.Quantity,
                Unit: inventoryItem.Unit,
                Price: inventoryItem.Price,
                CriticalStock: inventoryItem.CriticalStock,
                Store: inventoryItem.Store?._id || inventoryItem.Store,
                Category: inventoryItem.Category?._id || inventoryItem.Category
            };

            console.log("Payload:", payload); // Debug log

            const response = await fetch(`https://api.hgcdental.clinic/api/inventory/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json(); // Try to get error details from response
                throw new Error(errorData.message || 'Failed to update inventory item');
            }

            const updatedItem = await response.json();
            setInventoryItem(updatedItem);
            setIsEditable(false);
            setSuccessModalVisible(true);

            // 🔔 Check for low stock
            // Check if we need to send low stock notification
            if (updatedItem.Quantity < updatedItem.CriticalStock) {
                try {
                    alert(`⚠️ Inventory Alert: ${updatedItem.Name} is below critical stock. Current quantity: ${updatedItem.Quantity}, Critical level: ${updatedItem.CriticalStock}`);
                    // 1. Fetch the current user's profile
                    const profileResponse = await fetch('https://api.hgcdental.clinic/api/profile', {
                    });
                    const currentUser = await profileResponse.json();

                    // 2. Send notification only to the logged-in user
                    await fetch('https://api.hgcdental.clinic/api/notifications', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            type: 'low_stock',
                            message: `⚠️ Inventory Alert: ${updatedItem.Name} is below critical stock.`,
                            date: new Date().toISOString(),
                            receiver: currentUser._id, // Send to the logged-in user's ID
                        }),
                    });
                } catch (err) {
                    console.error('Failed to fetch profile or send notification:', err);
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInventoryItem((prev) => ({ ...prev, [name]: value }));
    };

    const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);
    const handleEditClick = () => setIsEditable(true);
    const closeSuccessModal = () => setSuccessModalVisible(false);

    if (loading) return <div>Loading...</div>;
    if (!inventoryItem) return <div>No inventory item found</div>;

    return (
        <AccessControl>
            <div className={`create-patient-container ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
                <InternalHeader moduleName="Inventory Item" toggleSidebar={toggleSidebar} />
                <div className="create-patient-content">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="create-patient">
                        <LinkMap
                            links={linkData}
                            moduleName="Category"
                            moduleIcon="inventory"
                        />
                        <div>
                            {isEditable ? (
                                <button className="create-patient-button" onClick={handleSaveClick}>
                                    <FaSave /> Save
                                </button>
                            ) : (
                                <button className="create-patient-button" onClick={handleEditClick}>Edit</button>
                            )}
                        </div>
                        <div className="create-patient-form">
                            <h2>Inventory Item Details</h2>

                            {/* Name Field */}
                            <div className="input-group">
                                <label className="label">Name</label>
                                <input
                                    type="text"
                                    name="Name"
                                    value={inventoryItem.Name || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                    required
                                />
                            </div>

                            {/* Quantity Field */}
                            <div className="input-group">
                                <label className="label">Quantity</label>
                                <input
                                    type="number"
                                    name="Quantity"
                                    value={inventoryItem.Quantity || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                    required
                                />
                            </div>

                            {/* Unit Field */}
                            <div className="input-group">
                                <label className="label">Unit</label>
                                <select
                                    name="Unit"
                                    value={inventoryItem.Unit || 'kg'}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                    required
                                >
                                    <option value="kg">kg</option>
                                    <option value="g">g</option>
                                    <option value="L">L</option>
                                    <option value="mL">mL</option>
                                    <option value="units">units</option>
                                    <option value="pieces">pieces</option>
                                </select>
                            </div>

                            {/* Price Field */}
                            <div className="input-group">
                                <label className="label">Price</label>
                                <input
                                    type="text"
                                    name="Price"
                                    value={
                                        isEditable
                                            ? inventoryItem.Price || ''
                                            : (inventoryItem.Price || 0).toLocaleString('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })
                                    }
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                    required
                                />
                            </div>

                            {/* Critical Stock Field */}
                            <div className="input-group">
                                <label className="label">Critical Stock</label>
                                <input
                                    type="number"
                                    name="CriticalStock"
                                    value={inventoryItem.CriticalStock || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                    required
                                />
                            </div>

                            {/* Store Dropdown Field */}
                            <div className="input-group">
                                <label className="label">Store</label>
                                <select
                                    name="Store"
                                    value={inventoryItem.Store?._id || inventoryItem.Store || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                    required
                                >
                                    <option value="">Select a store</option>
                                    {stores.map((store) => (
                                        <option key={store._id} value={store._id}>
                                            {store.StoreName} ({store.StoreContact})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Category Dropdown Field */}
                            <div className="input-group">
                                <label className="label">Category</label>
                                <select
                                    name="Category"
                                    value={inventoryItem.Category?._id || inventoryItem.Category || ""}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                    required
                                >
                                    <option value="">Select a category</option>
                                    {categories.map((category) => (
                                        <option key={category._id} value={category._id}>
                                            {category.Name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Success Modal */}
                        {successModalVisible && (
                            <div className="modal-overlay" onClick={closeSuccessModal}>
                                <div className="modal success-modal" onClick={(e) => e.stopPropagation()}>
                                    <h3>Success!</h3>
                                    <div className="modal-divider" />
                                    <p>Inventory item updated successfully.</p>
                                    <div className="modal-divider" />
                                    <button className="confirm-button" onClick={closeSuccessModal}>Close</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AccessControl>
    );
};

export default InventoryDetails;